<template>
  <ValidationObserver ref="observer" v-slot="{ passes }">
    <div class="font-16 dialog-retrieve pb-0" ref="formContainer">
      <v-form ref="submitForm" @submit.prevent="passes(onSubmit)">
        <v-alert v-if="retrieveQuoteError === true" color="var(--red-100)">
          <div class="d-flex flex-no-wrap error-message">
            <div class="mr-3">
              <v-icon color="var(--red-500)">mdi-alert-circle</v-icon>
            </div>
            <div>
              <span
                >Sorry, we couldn't retrieve a quote with these details. Please
                try again.</span
              >
            </div>
          </div>
        </v-alert>
        <p class="dialog-text">
          If you have previously had a quote and received a quote reference, you
          may enter your details below to retrieve your securely stored quote.
        </p>
        <div class="input-base">
          <BaseInput
            class="quote-reference resize-base-input"
            labelClass="font-18 font-weight-medium letter-spacing-4 black2-color"
            v-model="innerQuoteReference"
            data-uiid="QuoteReference"
            label="Quote reference"
            :rules="quoteReferenceRules"
          />
        </div>
        <div class="input-base">
          <BaseInput
            class="post-code resize-base-input"
            labelClass="font-18 font-weight-medium letter-spacing-4 black2-color"
            data-uiid="PostCode"
            label="Property postcode"
            v-model="postCode"
            :rules="postCodeRules"
          />
        </div>
        <div>
          <BaseLabel
            text="Date of birth"
            label-class="date-of-birth-label font-18 font-weight-medium mb-1 letter-spacing-4 black2-color"
          />
          <BaseInputDate
            class="resize-base-input"
            :hide-details="false"
            :height="48"
            :rules="dateOfBirthRules"
            width="unset"
            data-uiid="DateOfBirth"
            v-model="dateOfBirth"
            styleClass="d2c-date-input"
          />
        </div>
        <div class="button-container">
          <div style="width: 420px">
            <BaseButton
              text="RETRIEVE YOUR QUOTE"
              rounded
              block
              type="submit"
              class="font-weight-bold"
              loadingText="RETRIEVING..."
              height="60"
              textColor="black2-color"
              :color="'psorange'"
              :fontSize="'font-20'"
              :loading="isRetrieving"
            />
          </div>
        </div>
      </v-form>
    </div>
  </ValidationObserver>
</template>

<script>
import {
  checkIfQuoteNeedShowEligibility,
  isQuoteExpired,
} from "@/common/quotes";
import { addFieldLabel } from "@/common/utils";
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseInput from "@/components/BaseComponents/BaseInput.vue";
import BaseInputDate from "@/components/BaseComponents/BaseInputDate.vue";
import BaseLabel from "@/components/BaseComponents/BaseLabel.vue";
import { AGE_FOR_APPLICATION } from "@/constants/common.constants";
import { POST_CODE } from "@/constants/regex-patterns.constants";
import quoteService from "@/services/quote.service";
import { QUESTIONS_MODULE } from "@/store/types/module-types";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapGetters } from "vuex";

export default {
  name: "DialogContentRetrieveQuote",
  components: {
    BaseButton,
    BaseInputDate,
    BaseInput,
    BaseLabel,
    ValidationObserver,
  },
  props: {
    quoteReference: {
      type: String,
      default: null,
    },
    pageProductId: {
      type: Array,
      default: null,
    },
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, ["getAnswerObject"]),
  },
  data() {
    return {
      isRetrieving: false,
      retrieveQuoteError: null,
      dateOfBirth: "",
      dateOfBirthRules: addFieldLabel(
        {
          required: {
            errorMessageTemplate: "{0} must not be blank.",
          },
          date_valid: {
            strict: true,
            errorMessageTemplate: "{0} is not valid.",
          },
          age_in_range: {
            min: AGE_FOR_APPLICATION.min,
            max: AGE_FOR_APPLICATION.max,
            customMessage:
              "Date of birth must be between 18 and 110 years old.",
          },
        },
        "Date of birth"
      ),
      postCode: "",
      postCodeRules: addFieldLabel(
        {
          required: {
            errorMessageTemplate: "{0} must not be blank.",
          },
          max: {
            length: 10,
            errorMessageTemplate: "{0} is not valid.",
          },
          regex: {
            pattern: POST_CODE,
            errorMessageTemplate: "{0} is not valid.",
          },
        },
        "Property postcode"
      ),
      innerQuoteReference: "",
      quoteReferenceRules: addFieldLabel(
        {
          required: {
            errorMessageTemplate: "{0} must not be blank.",
          },
          max: {
            length: 24,
            errorMessageTemplate: "{0} may not be greater than {1} characters.",
          },
          regex: {
            pattern: "^\\s*PSL-[a-z]{2,9}-[0-9]{6,10}\\s*$",
            flags: "i",
            errorMessageTemplate: "{0} is not valid.",
          },
        },
        "Quote reference"
      ),
    };
  },
  watch: {
    quoteReference(value) {
      this.innerQuoteReference = value;
      this.resetRetrieveQuoteError();
    },
    innerQuoteReference() {
      this.resetRetrieveQuoteError();
    },
    postCode() {
      this.resetRetrieveQuoteError();
    },
    dateOfBirth() {
      this.resetRetrieveQuoteError();
    },
  },
  mounted() {
    this.innerQuoteReference = this.quoteReference;
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "setQuoteRequestIdAfterRetrieve",
      "setIsOriginalQuoteExpired",
    ]),
    resetRetrieveQuoteError() {
      if (this.retrieveQuoteError === true) {
        this.retrieveQuoteError = false;
      }
    },
    async onSubmit() {
      const isValid = await this.$refs.submitForm.validate();
      if (!isValid) {
        return;
      }
      try {
        this.retrieveQuoteError = false;
        this.isRetrieving = true;
        const result = await quoteService.retrieveQuoteByQuoteReference(
          this.innerQuoteReference?.trim(),
          this.postCode?.trim(),
          this.dateOfBirth,
          this.pageProductId
        );
        if (!result.ContinuationUrl) {
          this.retrieveQuoteError = true;
          return;
        }

        const continuationUrl = result.ContinuationUrl;

        this.setIsOriginalQuoteExpired(isQuoteExpired(result));
        const quoteAdditionalData = result.QuoteAdditionalData ?? {};
        let needShowEligibility = checkIfQuoteNeedShowEligibility(
          "DirectTenancyAgreement",
          result
        );

        if (needShowEligibility) {
          this.$emit(
            "showEligibility",
            continuationUrl,
            quoteAdditionalData.InsuredParty,
            quoteAdditionalData.CompanyName
          );
          return;
        }

        window.location.href = continuationUrl;
      } catch (ex) {
        this.retrieveQuoteError = true;
        this.$refs.formContainer.scrollIntoView({
          block: "start",
          scrollBehavior: "smooth",
        });
      } finally {
        this.isRetrieving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
tbody {
  tr:hover {
    background-color: transparent !important;
  }
}

tr:nth-of-type(odd) {
  background-color: #f5f5f5 !important;
}

.btn-file-downloaded {
  border: 1px solid white;
}

.input-label {
  font-size: 18px;
  font-weight: bold;
}

.dialog-retrieve {
  padding: 20px 40px 40px 37px;

  :deep(p.date-of-birth-label) {
    margin-bottom: 6px !important;
  }
}

.dialog-text {
  font-size: 16px;
  letter-spacing: 0.4px;
  color: #272727;
  margin-bottom: 31px;
}

.mg-bt-5px {
  margin-bottom: 5px;
}

.something-went-wrong {
  text-align: center;
  font-weight: 500;
  color: #ff5252;
  padding-bottom: 0.5rem;
}

.input-base {
  margin-bottom: 10px;
  :deep(.quote-reference) {
    max-width: 100%;
  }

  :deep(.post-code) {
    max-width: 274px;
  }
}

.date-label {
  margin-bottom: 6px;
}

.button-container {
  display: flex;
  justify-content: center;
  margin-top: 14px;
  overflow: auto;
}

@media screen and (max-width: $grid-breakpoints-sm) {
  .dialog-retrieve {
    padding: 19px 24px 40px 22px;
  }
}
</style>
